import React from 'react';
import { Box, Collapse, Flex, HStack, Text } from '@chakra-ui/react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import PropTypes from 'prop-types';

/**
 * The collapsible LABELS chip row, shared by every merchant picker so the
 * filter dropdown and the assignment modals stay visually identical.
 *
 * Controlled on purpose: MerchantFilterDropdown sizes its virtualised merchant
 * list from the height of the rows around it, so it has to own the expanded
 * flag rather than discover it after the fact.
 *
 * Multi-select - several labels can be active at once. Colour comes from each
 * label's own `color`, not a shared accent, so overlapping selections stay
 * distinguishable.
 */
const LabelFilterChips = ({
  labels,
  selectedLabelIds,
  isLoading,
  onLabelSelection,
  isExpanded,
  onToggleExpanded,
  isDisabled = false,
  emptyText = 'No labels yet',
}) => (
  <Box borderBottom="1px solid #f0f0f0" p="3">
    <Flex
      align="center"
      justify="space-between"
      cursor="pointer"
      onClick={onToggleExpanded}
    >
      <HStack spacing="2">
        <Text fontSize="0.75rem" fontWeight="700" color="gray.700">
          LABELS
        </Text>
        {labels.length > 0 && (
          <Text fontSize="0.7rem" fontWeight="600" color="gray.500">
            ({labels.length})
          </Text>
        )}
      </HStack>
      <HStack spacing="2">
        {selectedLabelIds.length > 0 && (
          <Box
            fontSize="0.65rem"
            fontWeight="700"
            color="blue.600"
            bg="blue.50"
            borderRadius="full"
            px="2"
            py="0.5"
          >
            {selectedLabelIds.length} active
          </Box>
        )}
        <Box color="gray.500" fontSize="10px">
          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
        </Box>
      </HStack>
    </Flex>

    <Collapse in={isExpanded} animateOpacity>
      <Flex
        wrap="wrap"
        gap="2"
        mt="2"
        maxH="150px"
        overflowY="auto"
        overflowX="hidden"
      >
        {isLoading ? (
          <Text fontSize="0.8rem" color="gray.500">
            Loading labels...
          </Text>
        ) : labels.length === 0 ? (
          <Text fontSize="0.8rem" color="gray.500">
            {emptyText}
          </Text>
        ) : (
          labels.map((label) => {
            const isSelected = selectedLabelIds.includes(label.id);
            return (
              <Flex
                key={label.id}
                align="center"
                gap="1.5"
                px="3"
                py="1"
                maxW="190px"
                borderRadius="6px"
                border="1px solid"
                borderColor={isSelected ? label.color : 'gray.200'}
                bg={isSelected ? `${label.color}1A` : 'gray.100'}
                color={isSelected ? label.color : 'gray.700'}
                fontSize="0.85rem"
                fontWeight="600"
                cursor={isDisabled ? 'not-allowed' : 'pointer'}
                opacity={isDisabled ? 0.6 : 1}
                _hover={isDisabled ? {} : { borderColor: label.color }}
                onClick={() => !isDisabled && onLabelSelection(label)}
                title={label.regionName}
              >
                <Box
                  w="8px"
                  h="8px"
                  flexShrink={0}
                  borderRadius="full"
                  bg={label.color}
                />
                <Text isTruncated minW="0" flex="1 1 auto">
                  {label.name}
                </Text>
              </Flex>
            );
          })
        )}
      </Flex>
    </Collapse>
  </Box>
);

LabelFilterChips.propTypes = {
  labels: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedLabelIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ).isRequired,
  isLoading: PropTypes.bool,
  onLabelSelection: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggleExpanded: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
  emptyText: PropTypes.string,
};

export default LabelFilterChips;
